import { Eye, EyeOff, Info, Search, X } from 'lucide-react';
import { forwardRef, InputHTMLAttributes, ReactNode, useId, useState } from 'react';
import styles from './Input.module.css';

// THE single-line text field — Figma "⚠️ Symphony DS" → Input, node 577:22622 ("Symphony
// Input", 577:22623, over `.Input Base` 577:22697, `.Form Label` 577:22739, `.Label Suffix`
// 577:22725 and `.Bottom Message` 577:22730). Never hand-roll an `<input>` with your own
// padding/radius/fill; see CLAUDE.md → "Inputs".
//
// The design ships 16 State×Skin symbols, but they are not 16 components: the STATES are what
// the DOM already knows (hover, focus, a value, `disabled`, `readOnly`, a validation failure),
// so they are CSS, and only the resting LOOK is a prop. That leaves four real axes — `kind`,
// `skin`, `size`, `status` — plus optional slots (`label`, `prefix`/`suffix`, `startIcon`,
// `onClear`, `showCount`). Everything else in the mock differs only by which slot is filled.
//
// The label belongs to the component rather than the caller: every panel that hand-rolled a
// `<label>` + `<Typography>` above a field picked its own type and gap, which is how the New
// Group panel came to wear 16px semibold labels where the DS asks for 14px medium.

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(' ');

/**
 * What the field is FOR. Shared 1:1 with native `SymphonyFieldKind`
 * (`Text, Secure, Email, Number, Search` — `email`/`number` are the DOM's own `type`, so web
 * spells them on `type=` rather than duplicating the axis).
 *
 * `secure` is the masked field: it drives `type` itself (`password` until the eye is flipped) and
 * brings the DS reveal toggle with it, so NO call site hand-rolls an eye. Both native platforms
 * have carried this since `SymphonyFieldKind.Secure` — web's `InputKind` was the odd one out, which
 * is why the change-password modal drew its own toggle out of two inline SVGs.
 *
 * `search` is an **excluded surface of the boxed-input spec**, not a skin — exactly the carve-out
 * native makes: a borderless elevated well at the *button* radius (12), never the bordered radius-8
 * box, so a page's search bar stays a search bar on all three surfaces. It also brings the leading
 * glass glyph and the trailing clear with it, so a call site supplies neither.
 */
export type InputKind = 'text' | 'secure' | 'search';

/** The resting LOOK of the box. Shared 1:1 with native `SymphonyFieldSkin`. */
export type InputSkin = 'border' | 'sunken';

/** Figma's Size axis on `.Input Base` — 42 / 36 / 30 tall, matching Button's large/medium/small. */
export type InputSize = 'large' | 'medium' | 'small';

/** Figma's `.Bottom Message` Type axis. `error` also paints the box; `warning` never does. */
export type InputStatus = 'default' | 'error' | 'warning';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  /**
   * What the field is for. `secure` masks the value and renders the DS reveal toggle (it OWNS
   * `type`, so a call site passes none). `search` renders the DS's excluded search surface
   * (borderless, radius 12) with its own leading glyph and clear button, and IGNORES `skin` — same
   * as native.
   */
  kind?: InputKind;
  /**
   * The resting look. `border` (default) rests transparent-filled behind a hairline outline;
   * `sunken` rests as a white borderless well, for a field on a grey/stone surface. The two
   * differ ONLY at rest — hover, focus, filled, error and disabled resolve identically.
   */
  skin?: InputSkin;
  /** `large` 42px (default, matches `Button size="large"`) · `medium` 36 · `small` 30. */
  size?: InputSize;
  /** The DS form label — 14/18 medium, 8px above the box, wired to it via `htmlFor`. */
  label?: ReactNode;
  /** The design's "(optional)" indicator beside the label. Renders nothing when absent. */
  optionalLabel?: ReactNode;
  /** Tooltip carried by the label's 18px info circle. No text, no circle. */
  labelInfo?: string;
  /** The design's `.Label Suffix` custom slot, end-aligned on the label row. */
  labelSuffix?: ReactNode;
  /** Renders the design's `.Label Suffix` counter ("8/100") from the value and `maxLength`. */
  showCount?: boolean;
  /** Quiet leading affix, e.g. "$". Not the value. */
  prefix?: ReactNode;
  /** Quiet trailing affix, e.g. "%". */
  suffix?: ReactNode;
  /** Leading glyph (16px), inside the box before the value. */
  startIcon?: ReactNode;
  /** Trailing control (a spinner, a button). Rendered after the design's icon group. */
  endAdornment?: ReactNode;
  /** Renders the design's round clear button when set and the field is non-empty. */
  onClear?: () => void;
  clearAriaLabel?: string;
  /**
   * `kind="secure"` only: fired with the NEW revealed state each time the eye is flipped, so a call
   * site can log a show/hide beacon without this component knowing anything about BI. Mirrors
   * native's `onRevealToggle` on both platforms.
   */
  onRevealToggle?: (revealed: boolean) => void;
  /** `kind="secure"`: the eye's accessible name while the value is masked, e.g. "Show password". */
  revealAriaLabel?: string;
  /** `kind="secure"`: the eye's accessible name while the value is visible, e.g. "Hide password". */
  hideAriaLabel?: string;
  /** `error` paints the border + message destructive; `warning` marks the message only. */
  status?: InputStatus;
  /** The 14/18 line under the box. Announced to AT when `status="error"`. */
  statusMessage?: ReactNode;
  className?: string;
}

/**
 * The resting/committed surface state, mirroring native's pure `inputStyle` resolver
 * (`inputStyle` in Android `ui/Components.kt`, `SymphonyInputState.style` on iOS) so the three
 * surfaces cannot drift on precedence: **disabled > read-only > error > filled > resting**.
 * Hover and focus are left to CSS — the DOM already knows them, and they sit between `filled`
 * and `resting`.
 */
export function inputState({
  disabled,
  readOnly,
  error,
  filled,
}: {
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  filled?: boolean;
}): 'disabled' | 'readOnly' | 'error' | 'filled' | 'resting' {
  if (disabled) return 'disabled';
  if (readOnly) return 'readOnly';
  if (error) return 'error';
  if (filled) return 'filled';
  return 'resting';
}

const STATE_CLASS = {
  disabled: styles.disabled,
  readOnly: styles.readOnly,
  error: styles.error,
  filled: styles.filled,
  resting: undefined,
} as const;

const SIZE_CLASS = { large: undefined, medium: styles.medium, small: styles.small } as const;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    kind = 'text',
    skin = 'border',
    size = 'large',
    label,
    optionalLabel,
    labelInfo,
    labelSuffix,
    showCount,
    prefix,
    suffix,
    startIcon,
    endAdornment,
    onClear,
    clearAriaLabel,
    onRevealToggle,
    revealAriaLabel,
    hideAriaLabel,
    status = 'default',
    statusMessage,
    className,
    disabled,
    readOnly,
    value,
    maxLength,
    id,
    type,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const statusId = useId();
  const inputId = id ?? generatedId;
  const isError = status === 'error';
  const text = String(value ?? '');
  const isSearch = kind === 'search';
  const isSecure = kind === 'secure';
  const [revealed, setRevealed] = useState(false);
  const state = inputState({ disabled, readOnly, error: isError, filled: text.length > 0 });
  // The clear slot is always laid out when `onClear` is given; only its visibility tracks the value,
  // so the field's width never changes as the user types.
  const showClear = !disabled && !readOnly && text.length > 0;
  // A secure field OWNS its `type` — masked until the eye is flipped — so a call site can't leave a
  // password rendering in the clear by passing `type="text"`. Every other kind takes the caller's.
  const resolvedType = isSecure ? (revealed ? 'text' : 'password') : type;
  const counter = showCount && maxLength !== undefined ? `${text.length}/${maxLength}` : undefined;
  const labelRow = label || optionalLabel || labelInfo || labelSuffix || counter;

  return (
    <div className={cx(styles.root, className)}>
      {labelRow ? (
        <div className={styles.labelRow}>
          <div className={styles.labelGroup}>
            <label className={styles.label} htmlFor={inputId}>
              {label}
              {optionalLabel ? <span className={styles.optional}>{optionalLabel}</span> : null}
            </label>
            {labelInfo ? (
              // The circle SAYS something, rather than only telling a screen reader. It shipped with an
              // `aria-label` and nothing else — sighted users hovered an icon that explained nothing — and
              // this is the component's first caller, so nobody had found it.
              //
              // A CSS bubble rather than the native `title`: `title` waits about a second, is unreachable
              // from the keyboard, and on an 18px target reads as "nothing happened". `tabIndex` + the
              // `:focus-visible` rule beside the `:hover` one is what makes it keyboard-equivalent.
              <span className={styles.labelInfoWrap} data-info={labelInfo} tabIndex={0} role="note" aria-label={labelInfo}>
                <Info className={styles.labelInfo} size={18} aria-hidden />
              </span>
            ) : null}
          </div>
          {labelSuffix ?? (counter ? <span className={styles.labelSuffix}>{counter}</span> : null)}
        </div>
      ) : null}

      <div
        className={cx(
          styles.box,
          isSearch ? styles.search : styles[skin],
          SIZE_CLASS[size],
          STATE_CLASS[state],
        )}
      >
        {isSearch ? <Search className={styles.searchIcon} size={18} aria-hidden /> : startIcon}
        {prefix ? <span className={styles.affix}>{prefix}</span> : null}
        <input
          {...rest}
          ref={ref}
          id={inputId}
          type={resolvedType}
          value={value}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={isError || undefined}
          aria-describedby={statusMessage ? statusId : undefined}
          className={styles.field}
        />
        {onClear || isSearch || isSecure ? (
          <span className={styles.iconGroup}>
            {/* The empty slot holds its 18px so the field cannot jump, which means the ✕ below stays
                MOUNTED with nothing to clear. `inert` (not `aria-hidden`) is what says so: pressing the
                ✕ empties the value, `showClear` flips false, and the button the pointer just focused
                would otherwise have been marked hidden-from-AT while still holding focus — the state
                Chrome blocks and logs. `inert` blurs it instead. See CLAUDE.md → "Hidden vs inert". */}
            {onClear || isSearch ? (
              <span className={cx(styles.clearSlot, !showClear && styles.empty)}>
                <button
                  type="button"
                  className={styles.clear}
                  onClick={onClear}
                  aria-label={clearAriaLabel}
                  inert={!showClear}
                  tabIndex={showClear ? undefined : -1}
                >
                  <X size={14} aria-hidden />
                </button>
              </span>
            ) : null}
            {/* The secure reveal — Lucide `eye`/`eye-off` at 16, in the secondary icon ink, bare
                (no pill behind it). The same glyphs, size and tint both native platforms ship, so a
                password field reads identically on all three. */}
            {isSecure ? (
              <button
                type="button"
                className={styles.reveal}
                onClick={() => {
                  const next = !revealed;
                  setRevealed(next);
                  onRevealToggle?.(next);
                }}
                aria-label={revealed ? hideAriaLabel : revealAriaLabel}
                aria-pressed={revealed}
                disabled={disabled}
              >
                {revealed ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
              </button>
            ) : null}
          </span>
        ) : null}
        {suffix ? <span className={styles.affix}>{suffix}</span> : null}
        {endAdornment}
      </div>

      {statusMessage ? (
        <span
          id={statusId}
          role={isError ? 'alert' : undefined}
          className={cx(styles.message, isError && styles.errorText)}
        >
          {status === 'error' ? <StatusAlertIcon className={styles.messageIcon} /> : null}
          {status === 'warning' ? <WarningAlertIcon className={styles.warningIcon} /> : null}
          {statusMessage}
        </span>
      ) : null}
    </div>
  );
});

// ── The `.Bottom Message` glyphs (Figma `Status alert`, 18x18 at x=0 with the text at x=22) ────────
//
// FILLED, not outlined. Figma draws the input-error glyph as a solid disc with the "!" punched out
// of it, and the warning as a solid triangle — which is also what iOS states in so many words
// (`AuthInlineError`: "a FILLED circle-alert … solid disc + punched-out '!'", drawn with
// `exclamationmark.circle.fill`). Lucide's `CircleAlert`/`TriangleAlert` are OUTLINE glyphs, so the
// message line used to read a full weight lighter than the design and than both native apps.
//
// The counter is punched with `fill-rule="evenodd"` rather than painted in the surface colour: the
// hole is then genuinely transparent, so the glyph survives being placed on a card, a modal or a
// tinted row instead of assuming the page white behind it.

function StatusAlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9 0a9 9 0 1 0 0 18A9 9 0 0 0 9 0Zm.9 4.35a.9.9 0 0 0-1.8 0v4.8a.9.9 0 0 0 1.8 0v-4.8ZM9 11.85a1.05 1.05 0 1 0 0 2.1 1.05 1.05 0 0 0 0-2.1Z"
      />
    </svg>
  );
}

function WarningAlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.7 1.85a1.5 1.5 0 0 1 2.6 0l7.09 12.4A1.5 1.5 0 0 1 16.09 16.5H1.91a1.5 1.5 0 0 1-1.3-2.25L7.7 1.85Zm2.2 4.6a.9.9 0 0 0-1.8 0v4.2a.9.9 0 0 0 1.8 0v-4.2ZM9 12.9a1.05 1.05 0 1 0 0 2.1 1.05 1.05 0 0 0 0-2.1Z"
      />
    </svg>
  );
}
